import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { SearchField } from "./SearchField";

type RelationItem = {
  id: number;
};

type RelationsEditorModalProps<TItem extends RelationItem> = {
  open: boolean;
  title: string;
  entityTitle: string;
  relationTitle: string;
  relationIcon: ReactNode;
  items: readonly TItem[];
  selectedIds: readonly number[];
  searchValue: string;
  searchPlaceholder: string;
  isFilteredEmpty: boolean;
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  loadingText: string;
  errorText: string;
  getItemLabel: (item: TItem) => string;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  onToggle: (id: number) => void;
  onSave: () => void;
  onClose: () => void;
};

export const RelationsEditorModal = <TItem extends RelationItem>({
  open,
  title,
  entityTitle,
  relationTitle,
  relationIcon,
  items,
  selectedIds,
  searchValue,
  searchPlaceholder,
  isFilteredEmpty,
  isLoading,
  isError,
  isSaving,
  loadingText,
  errorText,
  getItemLabel,
  onSearchChange,
  onSearchClear,
  onToggle,
  onSave,
  onClose,
}: RelationsEditorModalProps<TItem>) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 560,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          outline: "none",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ px: 3, py: 2.5 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            {title}
          </Typography>

          <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
            {entityTitle}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ px: 3, py: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              mb: 2,
            }}
          >
            {relationIcon}
            <Typography variant="h6">{relationTitle}</Typography>
          </Box>

          {isLoading && <Typography>{loadingText}</Typography>}

          {isError && <Typography color="error">{errorText}</Typography>}

          {!isLoading && !isError && (
            <>
              <SearchField
                value={searchValue}
                onChange={onSearchChange}
                onClear={onSearchClear}
                placeholder={searchPlaceholder}
                sx={{ mb: 2 }}
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  height: 320,
                  overflowY: "auto",
                  pl: 0.5,
                }}
              >
                {items.map((item) => (
                  <FormControlLabel
                    key={item.id}
                    control={
                      <Checkbox
                        checked={selectedIds.includes(item.id)}
                        onChange={() => onToggle(item.id)}
                      />
                    }
                    label={getItemLabel(item)}
                    sx={{
                      m: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: 20,
                      },
                    }}
                  />
                ))}

                {isFilteredEmpty && (
                  <Typography color="text.secondary">
                    Нет подходящих данных
                  </Typography>
                )}
              </Box>
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={isSaving || isLoading || isError}
              sx={{
                px: 4,
                py: 1.5,
                textTransform: "none",
                borderRadius: 1.5,
                bgcolor: "#2388A8",
                "&:hover": {
                  bgcolor: "#1d7895",
                },
              }}
            >
              {isSaving ? "Сохранение..." : "Сохранить"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
